import { Typography, Box, IconButton, CircularProgress, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTokenClaims } from '../security/useTokenClaims'
import { Employee } from '../types/Employee/Employee'
import { ShiftEndSelectSupervision } from '../components/reports/shift-end/ShiftEndSelectSupervision'
import { useNavigate, useParams } from 'react-router-dom'
import { getDirectReports } from '../services/shiftEndReportService'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useShiftEndContext } from '../components/reports/shift-end/ShiftEndContext'
import dayjs, { Dayjs } from 'dayjs'
import tz from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useProjectFeaturesContext } from '../components/project/contexts/ProjectFeaturesContext'
dayjs.extend(tz)

const ShiftEndSelectSupervisionSelectionPage = () => {
	const { id } = useTokenClaims()
	const [directReports, setDirectReports] = useState<Employee[]>()
	const [loading, setLoading] = useState(false)
	const [title, setTitle] = useState('')
	const [error, setError] = useState<string>()
	const params = useParams()
	const navigate = useNavigate()
	const { popFromNavPath, directReportNavPath, lastDirectReport, shiftDate, setShiftDate } = useShiftEndContext()
	const { projectTimeZone } = useProjectFeaturesContext()
	const loadData = async () => {
		setTitle('')
		setError(undefined)
		setLoading(true)

		try {
			const found = await getDirectReports(params.id ? parseInt(params.id) : id, 'C', shiftDate)
			if (found.length === 0) {
				setError('🔍 No direct reports found')
				setTitle('')
				setDirectReports([])
			} else {
				setTitle(found[0].role.title + 's')
				setDirectReports(found)
			}
		} catch (error) {
			setError('❌ Failed to load direct reports')
			setTitle('')
			setDirectReports([])
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		loadData()
	}, [params.id, shiftDate])
	const [firstRender, setFirstRender] = useState<boolean>(true)
	useEffect(() => {
		if (firstRender) {
			setFirstRender(false)
			return
		}
		if (directReportNavPath.length === 0) {
			navigate(`/reports`)
		} else {
			const last = lastDirectReport()
			navigate(`/reports/${last}`)
		}
	}, [directReportNavPath])
	return (
		<>
			<Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
				<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} paddingTop={'1rem'}>
					<Box display={'flex'} alignItems={'center'}>
						{params.id && (
							<IconButton
								color='default'
								onClick={() => {
									popFromNavPath()
								}}
							>
								<ArrowBackIcon sx={{ height: '2rem', width: 'auto' }} />
							</IconButton>
						)}
						<Typography
							sx={{
								fontSize: '1.5rem',
								paddingLeft: !params.id ? '1rem' : '0',
							}}
						>
							{title}
						</Typography>
					</Box>
					<Box sx={{ marginRight: '1rem', display: 'flex', gap: '1rem' }}>
						<DatePicker
							label='Pick a date'
							value={shiftDate}
							onChange={(date: Dayjs | null) => {
								if (date) {
									const utcDate = date.tz(projectTimeZone || 'UTC').utc()
									setShiftDate(utcDate.toDate())
								}
							}}
							shouldDisableDate={(date: Dayjs) => {
								const today = dayjs().startOf('day')
								const dateToCheck = date.startOf('day')
								return dateToCheck.isAfter(today)
							}}
							renderInput={(props) => <TextField {...props} />}
						/>
					</Box>
				</Box>
				<Box
					sx={{
						display: 'flex',
						flex: 1,
						overflowY: 'auto',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					{loading ? (
						<CircularProgress color='success' />
					) : error ? (
						<Typography color='error' variant='h6'>
							{error}
						</Typography>
					) : directReports?.length === 0 ? (
						<Typography variant='h6'>👥 No direct reports available</Typography>
					) : (
						<ShiftEndSelectSupervision foremen={directReports} postConfirmLoadData={loadData} />
					)}
				</Box>
			</Box>
		</>
	)
}

export default ShiftEndSelectSupervisionSelectionPage
